import * as moment from 'moment';

import { StoresEligibleForSplit, isStoreLineItemEditable } from '~/types/Store';

import type APIAddress from './APIAddress';
import type Address from './Address';
import type LineItem from './LineItem';
import type Parcel from './Parcel';
import type Transaction from './Transaction';

import * as APIError from './APIError';
import * as address from './Address';
import { requiresCustomsDeclaration as isCustomsRequired } from './CustomsDeclaration';
import * as lineItem from './LineItem';
import {
  batchEntryExtendedFromJSON,
  type BatchEntryExtended,
  type JSONBatchEntryExtended,
  type WebShipment,
} from './OrderBatchEntry';
import { BatchEntryStatus, type BatchEntryId } from './OrdersBatch';
import * as parcel from './Parcel';
import * as rates from './Rate';
import * as transactions from './Transaction';

interface BaseJSONOrder {
  address_from: address.JSONAddress;
  address_return: address.JSONAddress;
  created_at?: string; // v1 API does not have placed_at
  currency: Maybe<string>;
  errors: APIError.JSONAPIError[];
  hidden: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  line_items: lineItem.JSONLineItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes: Maybe<any>;
  object_id: string;
  object_owner: string;
  order_number: string;
  order_status: string;
  partial_fulfillment_requests?: JSONBatchEntryExtended[];
  placed_at: string;
  rates: rates.JSONBaseRate[];
  shipping_cost: string;
  shipping_cost_currency: string;
  shipping_method: string;
  shop_app: string;
  subtotal_price: Maybe<string>;
  to_address: address.JSONAddress;
  total_price: Maybe<string>;
  total_tax: string;
  // TODO: Revisit
  transactions: transactions.JSONTransaction[];
  validation_status: string;
  weight: Maybe<string>;
  weight_unit: Maybe<string>;
}

interface JSONOrder extends BaseJSONOrder {
  parcel?: parcel.JSONParcel;
}

interface SavedJSONOrder extends BaseJSONOrder {
  parcel?: parcel.SavedJSONParcel;
}

export type OrderTagIconType =
  | 'circle'
  | 'default'
  | 'diamond'
  | 'flag'
  | 'flower'
  | 'heart'
  | 'hourglass'
  | 'person'
  | 'puzzle'
  | 'sprout'
  | 'square'
  | 'star'
  | 'tree';

export type OrderTag = {
  color?: string;
  icon: OrderTagIconType;
  id: string;
  name: string;
};

export type OrderTagsMapping = {
  [key: string]: OrderTag[];
};

type OrderObjectId = string;

type OrderToBatchEntry = HashMap<string, number>;

type Order = {
  addressFrom?: Maybe<Address>;
  addressReturn?: Maybe<Address>;
  currency: Maybe<string>;
  errors: APIError.APIError[];
  extras?: {
    alcohol: boolean;
    dryIce: boolean;
    insurance: boolean;
    isBypassAddressValidation: boolean;
    isBypassCustoms: boolean;
    isResidential: boolean;
    isReturnRequested: boolean;
    packingSlipText: string;
    signatureConfirmation: boolean;
  };
  hidden: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  lineItems: LineItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes: Maybe<any>;
  objectId: OrderObjectId;
  objectOwner: string;
  orderNumber: string;
  orderStatus: OrderStatus;
  parcel?: Maybe<Parcel>;
  partialFulfillmentRequests: BatchEntryExtended[];
  placedAt: moment.Moment;
  rates: rates.BaseRate[];
  shippingCost: string;
  shippingCostCurrency: string;
  shippingMethod: string;
  shopApp: string;
  subtotalPrice: Maybe<string>;
  tags?: OrderTag[];
  toAddress: Maybe<Address>;
  totalPartialFulfillments: number;
  totalPrice: Maybe<string>;
  totalTax: string;
  // TODO: Revisit
  transactions: Transaction[];
  validationStatus: Maybe<OrderValidationStatus>;
  weight: Maybe<string>;
  weightUnit: Maybe<string>;
};

export type BaseWebOrder = {
  copied_from_order_object_id: string;
  created_at: string;
  currency: string;
  id?: number;
  notes: string;
  object_id: string;
  order_number: string;
  order_status: OrderStatus;
  shipping_cost: string;
  shipping_cost_currency: string;
  shipping_method: string;
  shop_app: string;
  subtotal_price: string;
  total_price: string;
  total_tax: string;
  weight: string;
  weight_unit: string;
};

export type WebOrder = {
  errors: APIError.APIError[];
  hidden: boolean;
  line_items: lineItem.WebLineItem[];
  object_owner: string;
  shipments: WebShipment[];
  tags?: OrderTag[];
  transactions: transactions.WebTransaction[];
  validation_status: Maybe<OrderValidationStatus>;
} & BaseWebOrder;

enum OrderStatus {
  AWAITPAY = 'AWAITPAY',
  CANCELLED = 'CANCELLED',
  GENERIC = 'GENERIC',
  INVALID = 'INVALID',
  PAID = 'PAID',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  REFUNDED = 'REFUNDED',
  SHIPPED = 'SHIPPED',
  UNKNOWN = 'UNKNOWN',
  VALID = 'VALID',
}

enum OrderValidationStatus {
  INVALID = 'INVALID',
  VALID = 'VALID',
}

enum OrderState {
  CANCELLED = 'CANCELLED',
  FULFILLED_MULTIPLE_LABELS = 'FULFILLED_MULTIPLE_LABELS',
  FULFILLED_OUTSIDE_SHIPPO = 'FULFILLED_OUTSIDE_SHIPPO',
  FULFILLED_SINGLE_LABEL = 'FULFILLED_SINGLE_LABEL',
  NOT_FULFILLED = 'NOT_FULFILLED',
  PARTIALLY_FULFILLED_MULTIPLE_LABELS = 'PARTIALLY_FULFILLED_MULTIPLE_LABELS',
  PARTIALLY_FULFILLED_SINGLE_LABEL = 'PARTIALLY_FULFILLED_SINGLE_LABEL',
  PARTIALLY_FULFILLED_SINGLE_REFUND = 'PARTIALLY_FULFILLED_SINGLE_REFUND', // denotes that refund action has been taken
  REFUND_PENDING = 'REFUND_PENDING',
  REFUND_REJECTED = 'REFUND_REJECTED',
  REFUNDED = 'REFUNDED',
}

export enum SyncStatus {
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
}

export type DuplicatedOrderStatus = {
  cloned_order_id: string;
  order_url: string;
  source_order_id: string;
  sync_status: SyncStatus;
};

// Types to render orders in the table
type PartialChild = {
  batchEntry: BatchEntryExtended;
  kind: 'partial-child';
  order: Order;
};

type PartialParent = {
  batchEntry: Maybe<BatchEntryExtended>;
  kind: 'partial-parent';
  order: Order;
};

type Single = {
  batchEntry: Maybe<BatchEntryExtended>;
  kind: 'single';
  order: Order;
};

export type OrdersRenderData = PartialChild | PartialParent | Single;

export type OrderSelection = HashMap<OrderObjectId, BatchEntryId[]>;

// From the order selection, get an object that contains the most specific IDs possible.
// If the order has batch entries, we get its batch entry ideas; otherwise, we get its order object id.
export const actionableOrderSelectionIds = (
  orderSelection: OrderSelection,
): {
  batchEntryIds: number[];
  orderObjectIds: string[];
} =>
  Object.entries(orderSelection).reduce<{
    batchEntryIds: number[];
    orderObjectIds: string[];
  }>(
    (obj, [orderObjectId, batchEntryIds]) => {
      if (batchEntryIds.length > 0)
        return { ...obj, batchEntryIds: [...obj.batchEntryIds, ...batchEntryIds] };

      return { ...obj, orderObjectIds: [...obj.orderObjectIds, orderObjectId] };
    },
    { batchEntryIds: [], orderObjectIds: [] },
  );

export interface SelectedOrdersAddresses {
  returnAddresses: address.BaseAddress[];
  senderAddresses: address.BaseAddress[];
}

const fromJSON = (jsonOrder: JSONOrder): Order => ({
  addressFrom: address.fromJSON(jsonOrder.address_from),
  addressReturn: address.fromJSON(jsonOrder.address_return),
  currency: jsonOrder.currency,
  errors: jsonOrder.errors.map(APIError.fromJSON),
  hidden: jsonOrder.hidden,
  items: jsonOrder.items,
  lineItems: lineItem.sort(
    jsonOrder.line_items.filter((lineItem) => lineItem.quantity > 0).map(lineItem.fromJSON),
  ),
  notes: jsonOrder.notes,
  objectId: jsonOrder.object_id,
  objectOwner: jsonOrder.object_owner,
  orderNumber: jsonOrder.order_number,
  orderStatus: <OrderStatus>jsonOrder.order_status,
  parcel: jsonOrder.parcel ? parcel.fromJSON(jsonOrder.parcel) : null,
  partialFulfillmentRequests: jsonOrder.partial_fulfillment_requests
    ? jsonOrder.partial_fulfillment_requests.map((x) => ({
        ...batchEntryExtendedFromJSON(x),
      }))
    : [],
  placedAt: moment(jsonOrder.placed_at || jsonOrder.created_at), // V1 uses created_at instead of placed_at
  rates: jsonOrder.rates ? jsonOrder.rates.map(rates.fromJSONBaseRate) : [],
  shippingCost: jsonOrder.shipping_cost,
  shippingCostCurrency: jsonOrder.shipping_cost_currency,
  shippingMethod: jsonOrder.shipping_method,
  shopApp: jsonOrder.shop_app,
  subtotalPrice: jsonOrder.subtotal_price,
  tags: undefined,
  toAddress: address.fromJSON(jsonOrder.to_address),
  totalPartialFulfillments: jsonOrder.partial_fulfillment_requests
    ? jsonOrder.partial_fulfillment_requests.length
    : 0,
  totalPrice: jsonOrder.total_price,
  totalTax: jsonOrder.total_tax,
  transactions: jsonOrder.transactions.map(transactions.fromJSON),
  validationStatus: <OrderValidationStatus>jsonOrder.validation_status,
  weight: jsonOrder.weight,
  weightUnit: jsonOrder.weight_unit,
});

const toJSON = (order: Order): Partial<SavedJSONOrder> => ({
  address_from: order.addressFrom ? address.toJSON(order.addressFrom) : undefined,
  address_return: order.addressReturn ? address.toJSON(order.addressReturn) : undefined,
  currency: order.currency,
  errors: order.errors,
  hidden: order.hidden,
  notes: order.notes,
  object_id: order.objectId,
  object_owner: order.objectOwner,
  order_number: order.orderNumber,
  order_status: order.orderStatus,
  parcel: order.parcel ? parcel.toJSON(order.parcel) : undefined,
  placed_at: order.placedAt.toISOString(),
  shipping_cost: order.shippingCost,
  shipping_cost_currency: order.shippingCostCurrency,
  shipping_method: order.shippingMethod,
  shop_app: order.shopApp,
  subtotal_price: order.subtotalPrice,
  // Casting partial back to address, it is fine if some fields are missing.
  to_address: order.toAddress ? address.toJSON(<Address>order.toAddress) : undefined,
  total_price: order.totalPrice,
  total_tax: order.totalTax,
  validation_status: order.validationStatus || '',
  weight: order.weight,
  weight_unit: order.weightUnit,
});

export const fromWebOrder = (webOrder: WebOrder): Order => {
  const txs = webOrder.transactions.map((t) => transactions.fromWebTransaction(t));
  const litems = webOrder.line_items.map((item) => lineItem.fromWebLineItem(item));
  const orderRates = webOrder.transactions.map((t) => rates.fromWebRate(t.rate));
  const firstShipment = webOrder.shipments.length > 0 ? webOrder.shipments[0] : undefined;
  const firstParcel =
    firstShipment && firstShipment?.parcels?.length > 0 ? firstShipment?.parcels[0] : undefined;

  return {
    addressFrom: firstShipment?.address_from
      ? address.fromWebAddress(firstShipment?.address_from)
      : null,
    addressReturn: firstShipment?.address_return
      ? address.fromWebAddress(firstShipment?.address_return)
      : null,
    currency: webOrder.currency,
    errors: webOrder.errors,
    hidden: webOrder.hidden,
    items: litems,
    lineItems: litems,
    notes: webOrder.notes,
    objectId: webOrder.object_id,
    objectOwner: webOrder.object_owner,
    orderNumber: webOrder.order_number,
    orderStatus: <OrderStatus>webOrder.order_status,
    parcel: firstParcel ? parcel.fromWebParcel(firstParcel) : null,
    //nulls ?
    partialFulfillmentRequests: [],
    placedAt: moment(webOrder.created_at), //Missing placedAt
    rates: orderRates,
    shippingCost: webOrder.shipping_cost,
    shippingCostCurrency: webOrder.shipping_cost_currency,
    shippingMethod: webOrder.shipping_method,
    shopApp: webOrder.shop_app,
    subtotalPrice: webOrder.subtotal_price,
    tags: webOrder.tags,
    toAddress: firstShipment?.address_to ? address.fromWebAddress(firstShipment?.address_to) : null,
    totalPartialFulfillments: 0,
    totalPrice: webOrder.total_price,
    totalTax: webOrder.total_tax,
    transactions: txs,
    validationStatus: <OrderValidationStatus>webOrder.validation_status,

    weight: webOrder.weight,
    weightUnit: webOrder.weight_unit,
  };
};

export const getOrderWeight = (order: Order): Maybe<number> =>
  (order.weight && parseFloat(order.weight)) || null;

export const orderHasWeight = (order: Order): boolean => {
  const orderWeight = order.weight && parseFloat(order.weight);

  return (
    (orderWeight && !isNaN(orderWeight) && orderWeight > 0 && !!order.weightUnit) ||
    (order.lineItems.length > 0 && order.lineItems.every(lineItem.lineItemHasWeight))
  );
};

export const isCancelled = (order: Order): boolean => order.orderStatus === OrderStatus.CANCELLED;

export const isFulfilled = (order: { orderStatus: OrderStatus }): boolean =>
  order.orderStatus === OrderStatus.SHIPPED;

export const isPartiallyFulfilled = (order: Order): boolean =>
  order.orderStatus === OrderStatus.PARTIALLY_FULFILLED;

const isRefundPending = (order: Order): boolean => {
  const successfulLiveTransactions = order.transactions.filter(
    (txn) => !txn.test && txn.status !== transactions.TransactionStatus.ERROR,
  );
  if (successfulLiveTransactions.length === 0) return false;

  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDPENDING,
  );
};

const isRefundRejected = (order: Order): boolean => {
  const successfulLiveTransactions = order.transactions.filter(
    (txn) => !txn.test && txn.status !== transactions.TransactionStatus.ERROR,
  );
  if (successfulLiveTransactions.length === 0) return false;

  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDREJECTED,
  );
};

const isRefunded = (order: Order): boolean => {
  const successfulLiveTransactions = order.transactions.filter(
    (txn) => !txn.test && txn.status !== transactions.TransactionStatus.ERROR,
  );
  if (successfulLiveTransactions.length === 0) return false;

  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDED,
  );
};

const isRefundedOutsideShippo = (order: Order): boolean =>
  order.orderStatus === OrderStatus.REFUNDED;

export const getOrderState = (order: Order): OrderState => {
  const outboundTransactions = order.transactions.filter(({ isReturn }) => !isReturn);

  if (isPartiallyFulfilled(order)) {
    if (outboundTransactions.length === 0) return OrderState.FULFILLED_OUTSIDE_SHIPPO;
    if (outboundTransactions.length === 1) {
      if (isRefunded(order) || isRefundRejected(order) || isRefundPending(order))
        return OrderState.PARTIALLY_FULFILLED_SINGLE_REFUND;

      return OrderState.PARTIALLY_FULFILLED_SINGLE_LABEL;
    }
    return OrderState.PARTIALLY_FULFILLED_MULTIPLE_LABELS;
  }

  if (isFulfilled(order) || isPartiallyFulfilled(order)) {
    if (outboundTransactions.length === 0) return OrderState.FULFILLED_OUTSIDE_SHIPPO;
    if (isRefunded(order)) return OrderState.REFUNDED;
    if (isRefundRejected(order)) return OrderState.REFUND_REJECTED;
    if (isRefundPending(order)) return OrderState.REFUND_PENDING;
    if (
      outboundTransactions.filter(
        (transaction) => transaction.status === transactions.TransactionStatus.SUCCESS,
      ).length === 1
    )
      return OrderState.FULFILLED_SINGLE_LABEL;

    return OrderState.FULFILLED_MULTIPLE_LABELS;
  }

  if (isCancelled(order)) return OrderState.CANCELLED;
  if (isRefundedOutsideShippo(order)) return OrderState.REFUNDED;

  return OrderState.NOT_FULFILLED;
};

export const isFulfilledOutsideShippo = (order: Order): boolean =>
  getOrderState(order) === OrderState.FULFILLED_OUTSIDE_SHIPPO;

// order is marked as fulfilled but does not have a parcel or a batch entry
export const isFulfilledInShippoOldExperience = (order: Order): boolean =>
  isFulfilled(order) && !order.parcel;

const manualOrderShopTypes: Partial<Record<string, boolean>> = {
  CSV_Import: true,
  Shippo: true,
};

export const isManualOrder = (order: Order): boolean => !!manualOrderShopTypes[order.shopApp];

export const requiresCustomsDeclaration = (
  order: Order,
  defaultFromAddress: Maybe<APIAddress>,
): boolean => {
  const addressFrom = (order && order.addressFrom) || defaultFromAddress; // guard against null order on hot reload
  const addressTo = order && order.toAddress;

  if (!addressTo || !addressFrom) return false;

  return isCustomsRequired(addressTo, addressFrom);
};

export const hasValidLineItems = (order: Order) => {
  const { lineItems } = order;
  return lineItems.length > 0 && lineItems.every(lineItem.isLineItemValid);
};

export const isOrderSplit = (order?: Order) => (order?.partialFulfillmentRequests || []).length > 0;

/**
 * Order is eligible for splitting if the order is
 * - not cancelled
 * - and the store is enabled for splitting
 */
export const isEligibleForSplit = ({
  lineItems,
  orderStatus,
  shopApp,
}: {
  lineItems: { quantity: number }[];
  orderStatus: 'CANCELLED' | string;
  shopApp: string;
}) => {
  // Special logic for Magento since back-end returns "Magento_2" but feron exlects "Magento 2"
  const isStoreEligibleForSplit = StoresEligibleForSplit.has(shopApp) || shopApp === 'Magento_2';

  if (orderStatus === OrderStatus.CANCELLED) return false;
  if (!isStoreEligibleForSplit) return false;
  return lineItems.reduce((currentTotal, item) => currentTotal + +item.quantity, 0) > 1;
};

export const isPartialFulfillmentAllCompleted = ({ partialFulfillmentRequests }: Order) =>
  partialFulfillmentRequests &&
  partialFulfillmentRequests.every(
    (batchEntry) => batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION,
  );

export const isOrderLineItemEditable = (order: Order): boolean =>
  !isOrderSplit(order) && isStoreLineItemEditable(order.shopApp) && !isFulfilled(order);

export const newOrder = (
  fromAddress?: address.JSONBaseAddress,
  toAddress?: address.JSONBaseAddress,
) => ({
  address_from: fromAddress || null,
  created_at: null,
  currency: null,
  notes: null,
  order_number: null,
  order_status: null,
  shipping_cost: null,
  shipping_cost_currency: null,
  shipping_method: null,
  shop_app: 'Shippo',
  subtotal_price: null,
  to_address: toAddress || null,
  total_price: null,
  total_tax: null,
  total_weight_unit: null,
  uses_line_items: false,
  validation_status: null,
  weight: null,
  weight_unit: null,
});
// calculates the total value (amount or weight) for a given line item based on input unit value and quantity
export const calculateValueByQuantity = (value: string, quantity: number): string =>
  (Number(value) * quantity).toString();

export const sortOrderTagsByName = (tags: OrderTag[]): OrderTag[] => {
  if (tags.length === 0) return [];

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return tags.sort((previousTag, nextTag) => collator.compare(previousTag.name, nextTag.name));
};

export const fromWebOrderToOrderTagsMapping = (webOrders: WebOrder[]): OrderTagsMapping => {
  if (!webOrders) return {};

  return webOrders.reduce(
    (orderTags: OrderTagsMapping, { object_id, tags }) => ({
      ...orderTags,
      [object_id]: tags || [],
    }),
    {},
  );
};

export default Order;
export {
  JSONOrder,
  OrderObjectId,
  OrderState,
  OrderStatus,
  OrderToBatchEntry,
  OrderValidationStatus,
  fromJSON,
  toJSON,
};
